import { PaymentType } from '@ace-clublabs/payment-types';
import React, { useCallback, useEffect, useState } from 'react';
import CardForm from '../components/CardForm';
import CardFormAyden from '../components/CardFormAdyen';
import ECheckForm from '../components/ECheckForm';
import ECheckFormAdyen from '../components/ECheckFormAdyen';
import { UtagHelper } from '../utils/utagHelper';
import { useGlobalState } from '../shared/state';
import { Pages } from '../shared/pages';
import { useNavigate } from 'react-router-dom';
import { ErrorType } from '../shared/types';
import { sources } from '../api/sources';
import Processing from '../components/Processing';
import LogCannon from '@ace-clublabs/log-cannon';
import { getPaymentPBLTaggingFlow, getPaymentPBLTaggingInsurancePolicies } from '../utils/methods';
import { validatePBLExpiration } from '../utils/pblExpirationHelper';

const PaymentPage: React.FC = () => {
  const [manifest] = useGlobalState('manifest');
  const [manifestId] = useGlobalState('manifestId');
  const [paymentType] = useGlobalState('paymentType');
  const [redirectMap] = useGlobalState('redirectMap');
  const [scheduledDate] = useGlobalState('scheduledDate');
  const [isScheduled] = useGlobalState('isScheduled');
  const [sessionId] = useGlobalState('sessionId');
  const [customerId] = useGlobalState('customerId');
  const [payByLinkData] = useGlobalState('payByLinkData');
  const [, setError] = useGlobalState('error');
  const [tagged, setTagged] = useState(false);
  const [showCardAdyen, setShowCardAdyen] = useState(false);
  const [showFDCardForm, setShowFDCardForm] = useState(false);
  const [isProcessing, setProcessing] = useState({
    status: true,
    text: 'Your checkout is loading',
    cindy: true,
    displayCheckmark: false,
    willRedirect: false,
    callback: () => {},
  });
  const navigate = useNavigate();

  /** Stable handler so Processing’s delayed onFinished always runs the latest callback (functional update). */
  const onProcessingFinished = useCallback(() => {
    setProcessing((prev) => {
      prev.callback?.();
      return {
        status: prev.willRedirect,
        text: '',
        cindy: false,
        displayCheckmark: false,
        willRedirect: false,
        callback: () => {},
      };
    });
  }, []);

  useEffect(() => {
    validatePBLExpiration(payByLinkData, navigate, setError);
  }, [payByLinkData, navigate, setError]);

  const gotoUrl = (url: string) => {
    window.location.href = url;
  };
  const _paymentType = manifest?.paymentType || paymentType;
  const showEcheckForm = _paymentType === PaymentType.Echeck;
  const showEcheckAdyenForm = !isScheduled && manifest?.clientConfig?.renderAdyenEcheckForm === true;

  const showError = !(
    _paymentType &&
    [PaymentType.CreditOrDebit, PaymentType.DebitOnly, PaymentType.Prepaid, PaymentType.Echeck].includes(_paymentType)
  );

  useEffect(() => {
    const validateManifest = async () => {
      if (!manifestId) {
        setError({ errorMessage: 'Session expired', errorType: ErrorType.SESSION_TIMEOUT });
        LogCannon.blast('Navigating to Session Error', {
          component: 'PaymentPage',
          manifestId,
          pathname: window.location.pathname,
        });
        navigate(Pages.Error);
        return;
      }
      try {
        await sources.getManifest(manifestId);
      } catch (e: any) {
        const isSessionTimeout = e?.type === ErrorType.SESSION_TIMEOUT;
        const errorMessage = 'ERROR: Getting manifest failed';
        LogCannon.blast(isSessionTimeout ? 'Navigating to Session Error' : 'Navigating to Error', {
          component: 'PaymentPage',
          errorMessage,
          error: typeof e === 'string' ? `${e}` : e,
          manifestId,
          ...(isSessionTimeout && { pathname: window.location.pathname }),
        });
        setError({
          errorMessage: e?.message || 'Unable to load payment session. Please try again',
          errorType: e?.type || ErrorType.SYSTEM_ERROR,
        });
        navigate(Pages.Error);
      }
    };
    validateManifest();
  }, []);

  useEffect(() => {
    if (!manifest) {
      LogCannon.blast('PaymentPage: manifest not yet loaded, skipping payment type validation', {
        component: 'PaymentPage',
        manifestId,
      });
      return;
    }
    if (showError) {
      const errorMessage = 'ERROR: Invalid or Unsupported Payment Type';
      LogCannon.blast('Navigating to Error', {
        component: 'PaymentPage',
        errorMessage,
        manifestId,
        paymentType: _paymentType,
      });
      navigate(Pages.Error);
    }
  }, [manifest, _paymentType]);

  useEffect(() => {
    const redirectUrl = redirectMap[manifestId!];
    if (redirectUrl) {
      gotoUrl(redirectUrl);
      return;
    }
  }, []);

  useEffect(() => {
    if (customerId && sessionId) {
      const flow = getPaymentPBLTaggingFlow(payByLinkData);
      const insurancePolicies = getPaymentPBLTaggingInsurancePolicies(payByLinkData);

      const smsPaymentTags = {
        channel: 'my account',
        subchannel1: 'sms',
        page: 'my account:sms:payment',
        sessionid: sessionId,
        custID: customerId,
        flow,
        ins_policy: insurancePolicies,
      };

      UtagHelper.tag(smsPaymentTags);
    }
  }, [customerId, sessionId]);

  useEffect(() => {
    if (
      _paymentType &&
      [PaymentType.CreditOrDebit, PaymentType.DebitOnly, PaymentType.Prepaid].includes(_paymentType)
    ) {
      if (!isScheduled && manifest?.clientConfig?.renderAdyenCardForm) {
        setShowCardAdyen(true);
        setShowFDCardForm(false);
      } else {
        setShowCardAdyen(false);
        setShowFDCardForm(true);
      }
    }

    if (!manifest?.tags || tagged) {
      return;
    }
    const { tags, clientConfig } = manifest || {};
    const { channel, subchannel1, subchannel2 } = tags || {};

    const currentPage = 'payment';
    const updatedTags = tags?.page
      ? UtagHelper.tagPage(tags, currentPage)
      : {
          ...tags,
          page: [channel, subchannel1, subchannel2, currentPage].filter(Boolean).join(':'),
        };

    const allowedPaymentTypes = clientConfig?.allowedPaymentTypes || [];
    const payment_options = allowedPaymentTypes?.sort().join() || '';
    UtagHelper.tag({ ...updatedTags, sessionid: sessionId, payment_options });
    setTagged(true);
  }, [manifest, paymentType]);

  return (
    <>
      {isProcessing.status && (
        <Processing
          text={isProcessing.text}
          renderCindy={isProcessing.cindy}
          displayCheckmark={isProcessing.displayCheckmark}
          onFinished={onProcessingFinished}
        />
      )}
      {showFDCardForm && (
        <CardForm
          manifestId={manifestId!}
          amount={manifest?.amount!}
          paymentType={manifest?.paymentType || PaymentType.Undefined}
          autoPay={manifest?.autoPay}
          returnUrl={manifest?.redirect?.returnUrl!}
          tokenOnly={manifest?.tokenOnly}
          tags={manifest?.tags}
          appId={manifest?.appId}
          softErrorCode={manifest?.softErrorCode}
          scheduledDate={scheduledDate}
          isScheduled={isScheduled}
          paymentFormSubtitle={manifest?.clientConfig?.paymentFormSubtitle}
          setParentProcessing={setProcessing}
        />
      )}
      {showCardAdyen && (
        <CardFormAyden
          manifestId={manifestId!}
          amount={manifest?.amount!}
          paymentType={manifest?.paymentType || PaymentType.Undefined}
          autoPay={manifest?.autoPay}
          returnUrl={manifest?.redirect?.returnUrl!}
          tokenOnly={manifest?.tokenOnly}
          tags={manifest?.tags}
          appId={manifest?.appId}
          softErrorCode={manifest?.softErrorCode}
          scheduledDate={scheduledDate}
          isScheduled={isScheduled}
          paymentFormSubtitle={manifest?.clientConfig?.paymentFormSubtitle}
          setParentProcessing={setProcessing}
          isUnitTest={false}
        />
      )}
      {showEcheckForm &&
        (showEcheckAdyenForm ? (
          <ECheckFormAdyen
            manifestId={manifestId!}
            amount={manifest?.amount!}
            autoPay={manifest?.autoPay}
            returnUrl={manifest?.redirect.returnUrl!}
            appId={manifest?.appId}
            scheduledDate={scheduledDate}
            isScheduled={isScheduled}
            tokenOnly={manifest?.tokenOnly}
            paymentFormSubtitle={manifest?.clientConfig?.paymentFormSubtitle}
            setParentProcessing={setProcessing}
            hideWhileParentProcessing={isProcessing.status}
            softErrorCode={manifest?.softErrorCode}
          />
        ) : (
          <ECheckForm
            manifestId={manifestId!}
            amount={manifest?.amount!}
            autoPay={manifest?.autoPay}
            returnUrl={manifest?.redirect.returnUrl!}
            appId={manifest?.appId}
            scheduledDate={scheduledDate}
            isScheduled={isScheduled}
            paymentFormSubtitle={manifest?.clientConfig?.paymentFormSubtitle}
            setParentProcessing={setProcessing}
            softErrorCode={manifest?.softErrorCode}
          />
        ))}
      {showError && <></>}
    </>
  );
};

export default PaymentPage;
